import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Divider,
  Chip,
  CircularProgress,
  TablePagination,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import axios from "axios";
import { ContainedButton, OutlinedButton } from "../../Common/Buttons";
import {
  showToast,
  ACCREDITEDKYCWEBSERVICE,
  CACHESERVICE,
} from "../../CoreDataService";

const InvestorInterestAccruedWallet = () => {
  const { btncolor } = useSelector((state) => state);
  const investorHomeCurrency = localStorage.getItem("homeCurrency") || "INR";
  const currencySymbol = localStorage.getItem("homeCurrencySymbol") || "₹";

  // investorId is the logged-in user's userId — same storage key used across
  // the app (e.g. MyWalletSpot.js getUserTransactionBalanceRest).
  const investorId = localStorage.getItem("userId");
  const uuid = localStorage.getItem("uuid");
  const accessToken = localStorage.getItem("access_token");
  // The wallet operates in the investor's Home Currency, so the transfer call
  // uses the account's homeCurrencyId (BRS §17), same as the Issuer side.
  const homeCurrencyId = localStorage.getItem("homeCurrencyId");

  const [loading, setLoading] = useState(true);
  const [securityDetails, setSecurityDetails] = useState([]);
  const [assetMap, setAssetMap] = useState({});
  const [totalAccrued, setTotalAccrued] = useState(0);
  const [totalAvailable, setTotalAvailable] = useState(0);

  const [transferModal, setTransferModal] = useState(false);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferAmount, setTransferAmount] = useState("");

  const [redemptionHistory, setRedemptionHistory] = useState([]);
  const [redemptionLoading, setRedemptionLoading] = useState(true);
  const [redemptionTotal, setRedemptionTotal] = useState(0);
  const [redemptionPage, setRedemptionPage] = useState(0);
  const [redemptionPageSize, setRedemptionPageSize] = useState(10);

  useEffect(() => {
    getAssetMap();
    getInvestorInterestAccruedWallet();
  }, []);

  useEffect(() => {
    getInvestorRedemptionHistory();
  }, [redemptionPage, redemptionPageSize]);

  const getAssetMap = () => {
    axios
      .get(
        `${CACHESERVICE}/getAssetsData?Name=Assets&BrokerId=${localStorage.getItem(
          "BROKERID"
        )}`
      )
      .then((response) => {
        const currencies = JSON.parse(response.data.value).Values;
        const map = {};
        currencies.forEach((c) => {
          map[c.currencyId] = c.currencyCode;
        });
        setAssetMap(map);
      })
      .catch(() => {});
  };

  const getInvestorInterestAccruedWallet = () => {
    setLoading(true);
    axios
      .get(
        `${ACCREDITEDKYCWEBSERVICE}accreditation/get-investor-interest-accrued-wallet`,
        {
          params: { uuid, investorId },
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + accessToken,
          },
        }
      )
      .then((response) => {
        const res = response?.data;
        if (!res?.success) {
          showToast(
            "error",
            res?.message || "Could not load Interest Accrued Wallet details"
          );
          return;
        }
        const data = res.data || {};
        setSecurityDetails(data.securityDetails || []);
        setTotalAccrued(data.totalAccrued || 0);
        setTotalAvailable(data.availableToTransfer || 0);
      })
      .catch((reason) => {
        if (reason?.response?.data?.error === "invalid_token") {
          showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
        } else {
          showToast("error", "Could not connect to server");
        }
      })
      .finally(() => setLoading(false));
  };

  const getInvestorRedemptionHistory = () => {
    setRedemptionLoading(true);
    axios
      .get(
        `${ACCREDITEDKYCWEBSERVICE}accreditation/get-investor-redemption-history`,
        {
          params: {
            uuid,
            investorId,
            pageNumber: redemptionPage + 1,
            pageSize: redemptionPageSize,
          },
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + accessToken,
          },
        }
      )
      .then((response) => {
        const res = response?.data;
        if (!res?.success) {
          showToast(
            "error",
            res?.message || "Could not load Redemption History"
          );
          return;
        }
        const data = res.data || {};
        setRedemptionHistory(data.data || []);
        setRedemptionTotal(data.totalRecords || 0);
      })
      .catch((reason) => {
        if (reason?.response?.data?.error === "invalid_token") {
          showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
        } else {
          showToast("error", "Could not connect to server");
        }
      })
      .finally(() => setRedemptionLoading(false));
  };

  const redemptionStatusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "completed":
      case "paid":
        return "#16a34a";
      case "processing":
      case "pending":
        return "#f59e0b";
      case "failed":
      case "rejected":
        return "#dc2626";
      default:
        return "#6b7280";
    }
  };

  const byAsset = useMemo(
    () =>
      securityDetails.map((s) => ({
        securityId: s.securityId,
        assetId: s.assetId,
        asset: assetMap[s.assetId] || `Asset #${s.assetId}`,
        accrued: s.accruedInterest || 0,
        available: s.availableToTransfer || 0,
        nextPayment: s.nextPaymentDate || "—",
      })),
    [securityDetails, assetMap]
  );

  // No FX-estimate flag is returned by the API yet — kept off until the
  // backend exposes whether a security's accrual needs currency conversion.
  const hasEstimated = false;

  const fmt = (n) =>
    `${currencySymbol}${Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const handleCloseTransferModal = () => (
    setTransferModal(false), setTransferAmount("")
  );

  const onlyAmount = (e) => {
    setTransferAmount(e.target.value.replace(/[^0-9.]/g, ""));
  };

  const submitTransfer = () => {
    const amt = parseFloat(transferAmount);
    if (!amt || amt <= 0) {
      showToast("error", "Enter a valid amount to transfer");
      return;
    }
    // Only "Available to Transfer" (already-paid interest) can move to Spot
    // Wallet — unpaid Accrued Interest is never transferable (BRS §18–19).
    if (amt > totalAvailable) {
      showToast(
        "error",
        `You can only transfer up to your Available to Transfer balance of ${fmt(
          totalAvailable
        )}`
      );
      return;
    }
    if (!homeCurrencyId) {
      showToast("error", "Home Currency could not be determined. Please try again.");
      return;
    }

    setTransferSubmitting(true);
    axios
      .post(
        `${ACCREDITEDKYCWEBSERVICE}accreditation/transfer-to-spot-wallet-by-investor`,
        {
          uuid,
          investorId,
          currencyId: homeCurrencyId,
          amount: amt,
        },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + accessToken,
          },
        }
      )
      .then((response) => {
        const res = response?.data;
        if (!res?.success) {
          showToast("error", res?.message || "Transfer failed");
          return;
        }
        showToast("success", res.message || "Amount transferred successfully to Spot Wallet.");
        handleCloseTransferModal();
        getInvestorInterestAccruedWallet();
      })
      .catch((reason) => {
        if (reason?.response?.data?.error === "invalid_token") {
          showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
        } else {
          showToast("error", reason?.response?.data?.message || "Could not connect to server");
        }
      })
      .finally(() => setTransferSubmitting(false));
  };

  const statsMeta = [
    {
      label: "Accrued Interest",
      value: fmt(totalAccrued),
      icon: <TrendingUpOutlinedIcon sx={{ fontSize: 20 }} />,
      color: btncolor,
    },
    {
      label: "Available to Transfer",
      value: fmt(totalAvailable),
      icon: <SavingsOutlinedIcon sx={{ fontSize: 20 }} />,
      color: "#16a34a",
    },
  ];

  return (
    <Box sx={pageWrapper}>
      {/* Hero */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(
            btncolor || "#1976d2",
            0.95
          )} 0%, ${alpha(btncolor || "#1976d2", 0.72)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <SavingsOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box>
            <Typography sx={heroTitle}>
              Interest Accrued Wallet
              <Typography component="span" sx={heroCurrencyTag}>
                {investorHomeCurrency}
              </Typography>
            </Typography>
            <Typography sx={heroSubtitle}>
              Interest earned across every Security you hold, in{" "}
              {investorHomeCurrency}. Paid interest becomes available here
              before moving to your Spot Wallet.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={statsStrip}>
        {statsMeta.map((s, i) => (
          <Box key={i} sx={statCard}>
            <Box
              sx={{
                ...statIconWrap,
                backgroundColor: alpha(s.color || "#1976d2", 0.1),
                color: s.color,
              }}
            >
              {s.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={statLabel}>{s.label}</Typography>
            </Box>
            <Box sx={statValueWrap}>
              <Typography sx={{ ...statValue, color: s.color }}>
                {s.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {hasEstimated && (
        <Box sx={infoBanner}>
          <InfoOutlinedIcon sx={{ color: "#3b82f6", fontSize: 20 }} />
          <Typography sx={infoBannerText}>
            Amounts marked <strong>Estimated</strong> are calculated using
            today's exchange rate. The final {investorHomeCurrency} amount is
            fixed only when the Issuer processes that interest payment.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
        <ContainedButton
          onClick={() => setTransferModal(true)}
          disabled={totalAvailable <= 0}
          sx={{ height: 40, px: 2.5, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
        >
          <SwapHorizOutlinedIcon sx={{ fontSize: 18, mr: 0.75 }} />
          Transfer to Spot Wallet
        </ContainedButton>
      </Box>

      {/* By Security */}
      <Box sx={mainCard}>
        <Box sx={sectionHeaderWrap}>
          <Typography sx={sectionHeaderTitle}>By Security</Typography>
        </Box>
        <TableContainer component={Paper} sx={modernTableContainer}>
          <Table sx={tableStyle}>
            <TableHead>
              <TableRow sx={modernTableHeadRow}>
                {["Asset", "Accrued Interest", "Available", "Next Payment"].map((col) => (
                  <TableCell key={col} sx={modernTableHeadCell}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} sx={emptyCell}>
                    <CircularProgress size={28} sx={{ color: "var(--text-color)", opacity: 0.6 }} />
                  </TableCell>
                </TableRow>
              ) : byAsset.length > 0 ? (
                byAsset.map((row, i) => (
                  <TableRow key={row.securityId ?? i} sx={modernTableBodyRow}>
                    <TableCell sx={{ ...modernTableBodyCell, fontWeight: 700 }}>
                      {row.asset}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {fmt(row.accrued)}
                      {row.estimated && (
                        <Chip
                          label="Estimated"
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            backgroundColor: alpha("#3b82f6", 0.12),
                            color: "#3b82f6",
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ ...modernTableBodyCell, color: "#16a34a", fontWeight: 600 }}>
                      {fmt(row.available)}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.nextPayment}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={emptyCell}>
                    <InboxOutlinedIcon sx={emptyIcon} />
                    <Typography sx={emptyText}>
                      You are not yet holding any interest-bearing Security.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Interest history — no backing API yet; shown as an empty state
          rather than fixture data until that endpoint exists. */}
      <Box sx={{ ...mainCard, mt: 3 }}>
        <Box sx={sectionHeaderWrap}>
          <Typography sx={sectionHeaderTitle}>Interest History</Typography>
        </Box>
        <Box sx={emptyCell}>
          <InboxOutlinedIcon sx={emptyIcon} />
          <Typography sx={emptyText}>
            Your interest history will appear here once a Security you hold
            starts accruing interest.
          </Typography>
        </Box>
      </Box>

      {/* Redemption History */}
      <Box sx={{ ...mainCard, mt: 3 }}>
        <Box sx={sectionHeaderWrap}>
          <Typography sx={sectionHeaderTitle}>Redemption History</Typography>
        </Box>
        <TableContainer component={Paper} sx={modernTableContainer}>
          <Table sx={tableStyle}>
            <TableHead>
              <TableRow sx={modernTableHeadRow}>
                {[
                  "Security",
                  "Quantity Redeemed",
                  "Redemption Price",
                  "Principal Received",
                  "Transaction Ref",
                  "Status",
                  "Maturity Date",
                  "Payment Date",
                  "Created On",
                ].map((col) => (
                  <TableCell key={col} sx={modernTableHeadCell}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {redemptionLoading ? (
                <TableRow>
                  <TableCell colSpan={9} sx={emptyCell}>
                    <CircularProgress size={28} sx={{ color: "var(--text-color)", opacity: 0.6 }} />
                  </TableCell>
                </TableRow>
              ) : redemptionHistory.length > 0 ? (
                redemptionHistory.map((row, i) => (
                  <TableRow key={row.receiptId ?? i} sx={modernTableBodyRow}>
                    <TableCell sx={{ ...modernTableBodyCell, fontWeight: 700 }}>
                      {row.security}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {row.quantityRedeemed}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {Number(row.redemptionPrice || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      {row.currency}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {Number(row.principalReceived || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      {row.currency}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {row.transactionRef || "—"}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      <Chip
                        label={row.status || "—"}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          backgroundColor: alpha(redemptionStatusColor(row.status), 0.12),
                          color: redemptionStatusColor(row.status),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {row.maturityDate || "—"}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {row.paymentDate || "—"}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {row.createdOn || "—"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} sx={emptyCell}>
                    <InboxOutlinedIcon sx={emptyIcon} />
                    <Typography sx={emptyText}>
                      You have not redeemed any Security yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={redemptionTotal}
          page={redemptionPage}
          onPageChange={(e, newPage) => setRedemptionPage(newPage)}
          rowsPerPage={redemptionPageSize}
          rowsPerPageOptions={[10, 25, 50]}
          onRowsPerPageChange={(e) => {
            setRedemptionPageSize(parseInt(e.target.value, 10));
            setRedemptionPage(0);
          }}
          sx={paginationStyle}
        />
      </Box>

      {/* Transfer to Spot Wallet */}
      <Dialog
        open={transferModal}
        onClose={handleCloseTransferModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={dialogTitleStyle}>
          Transfer to Spot Wallet
          <IconButton onClick={handleCloseTransferModal} size="small" sx={dialogCloseBtn}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogContent sx={dialogContentStyle}>
          <Box sx={redeemInfoGrid}>
            <Box sx={redeemInfoRow}>
              <Typography sx={redeemInfoLabel}>Available to Transfer</Typography>
              <Typography sx={redeemInfoValue}>{fmt(totalAvailable)}</Typography>
            </Box>
            <Divider sx={{ borderColor: "rgba(125,125,125,0.1)" }} />
            <Box sx={redeemInfoRow}>
              <Typography sx={redeemInfoLabel}>Accrued Interest (unpaid)</Typography>
              <Typography sx={redeemInfoValue}>{fmt(totalAccrued)}</Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={fieldLabelSmall}>
              Amount ({investorHomeCurrency})
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter amount"
              value={transferAmount}
              onChange={onlyAmount}
              sx={dialogTextField}
            />
          </Box>
          <Typography sx={{ ...dialogText, mt: 2, opacity: 0.7 }}>
            * Only interest already paid by the Issuer (Available to Transfer)
            can move to your Spot Wallet. Withdraw from there using your
            existing Spot Wallet withdrawal flow.
          </Typography>
        </DialogContent>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogActions sx={dialogActionsStyle}>
          <ContainedButton
            fullWidth
            onClick={submitTransfer}
            disabled={transferSubmitting}
            sx={{ mx: 1 }}
          >
            {transferSubmitting ? "Submitting..." : "Submit"}
          </ContainedButton>
          <OutlinedButton onClick={handleCloseTransferModal} sx={{ mx: 1 }}>
            Cancel
          </OutlinedButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Styles (shared visual language with the Earn Wallet page) ──────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const heroCurrencyTag = {
  fontSize: "0.72rem",
  fontWeight: 700,
  color: "#fff",
  backgroundColor: "rgba(255,255,255,0.22)",
  border: "1px solid rgba(255,255,255,0.3)",
  borderRadius: "6px",
  px: 1,
  py: 0.25,
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 620,
};

const statsStrip = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 2,
  mb: 2,
};

const statCard = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  p: 2,
  backgroundColor: "var(--body-color-less)",
  border: "1px solid rgba(125,125,125,0.15)",
  borderRadius: "14px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
  },
};

const statIconWrap = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const statLabel = {
  fontSize: "0.82rem",
  fontWeight: 600,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const statValueWrap = {
  textAlign: "right",
  flexShrink: 0,
};

const statValue = {
  fontSize: "1.2rem",
  fontWeight: 700,
  lineHeight: 1,
};

const infoBanner = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.25,
  border: "1px solid",
  borderColor: alpha("#3b82f6", 0.3),
  backgroundColor: alpha("#3b82f6", 0.07),
  borderRadius: "12px",
  p: 1.75,
  mb: 3,
};

const infoBannerText = {
  fontSize: "0.8rem",
  color: "var(--text-color)",
  opacity: 0.85,
  lineHeight: 1.6,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const sectionHeaderWrap = {
  px: { xs: 2, md: 3 },
  py: 2,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  backgroundColor: "var(--body-color-less2)",
};

const sectionHeaderTitle = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "var(--text-color)",
};

const modernTableContainer = {
  borderRadius: 0,
  border: "none",
  boxShadow: "none",
  overflowX: "auto",
  backgroundColor: "var(--body-color-less)",
};

const tableStyle = {
  minWidth: 760,
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
  whiteSpace: "nowrap",
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
  maxWidth: 400,
  mx: "auto",
  lineHeight: 1.6,
};

const paginationStyle = {
  borderTop: "1px solid rgba(125,125,125,0.15)",
  backgroundColor: "var(--body-color-less2)",
  color: "var(--text-color)",
  "& .MuiTablePagination-toolbar": {
    minHeight: 52,
    color: "var(--text-color)",
  },
  "& .MuiTablePagination-displayedRows": {
    fontSize: "0.82rem",
    color: "var(--text-color)",
  },
  "& .MuiTablePagination-selectLabel": { color: "var(--text-color)" },
  "& .MuiSvgIcon-root": { color: "var(--text-color)" },
  "& .MuiIconButton-root.Mui-disabled .MuiSvgIcon-root": { opacity: 0.35 },
  "& .MuiSelect-select": { color: "var(--text-color)" },
};

const dialogPaper = {
  borderRadius: "16px",
  minWidth: { xs: "85%", sm: 440 },
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  overflow: "hidden",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.05rem",
  py: 2.25,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
  position: "relative",
};

const dialogCloseBtn = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--text-color)",
  opacity: 0.6,
};

const dialogContentStyle = {
  pt: 3,
  pb: 2,
  px: { xs: 2.5, md: 3 },
  backgroundColor: "var(--popupbody-color)",
};

const dialogText = {
  fontSize: "0.88rem",
  color: "var(--text-color)",
  opacity: 0.85,
  lineHeight: 1.65,
};

const dialogActionsStyle = {
  justifyContent: "center",
  px: 3,
  pb: 2.5,
  backgroundColor: "var(--popupbody-color)",
};

const redeemInfoGrid = {
  backgroundColor: "var(--body-color-less2)",
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  overflow: "hidden",
};

const redeemInfoRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 2,
  py: 1.5,
};

const redeemInfoLabel = {
  fontSize: "0.82rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

const redeemInfoValue = {
  fontSize: "0.88rem",
  fontWeight: 700,
  color: "var(--text-color)",
};

const fieldLabelSmall = {
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "var(--text-color)",
  opacity: 0.6,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  mb: 0.75,
};

const dialogTextField = {
  "& .MuiInputBase-root": {
    borderRadius: "10px",
    backgroundColor: "var(--body-color-less2)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.45)",
  },
};

export default InvestorInterestAccruedWallet;
